import { Box, FormGroup, FormHelperText, FormLabel, InputAdornment, MenuItem, Select, Stack, TextField, Typography, useTheme } from '@mui/material';
import React from 'react';
import { Controller } from 'react-hook-form';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import { formatPhoneNumberIntl, isPossiblePhoneNumber } from 'react-phone-number-input/input';

type Props = {
    name: string;
    control: any;
    defaultValue?: string;
    required?: boolean;
    label?: string;
    disabled?: boolean;
    viewOnly?: boolean;
    placeholderTextColor?: any;
    placeholder?: string;
    textAlign?: any;
    inputMask?: any;
    inputStyle?: any;
    countryCode?: string;
    hide?: boolean;
    handleHide?: any;
    helperText?: string;
};

const UncontrolledInputPhone = React.forwardRef(({ value, onChange, error, ...props }: any, ref) => {
    const theme = useTheme();
    const { inputValue, handlePhoneValueChange, country, setCountry } = usePhoneInput({
        defaultCountry: 'fr',
        value: value?.indexOf(' ') > -1 ? value.split(' ')[1] : value || '',
        countries: defaultCountries,
        onChange: data => {
            onChange(data.phone);
        },
        disableDialCodeAndPrefix: true,
    });

    return (
        <TextField
            variant="outlined"
            color="primary"
            value={inputValue}
            onChange={handlePhoneValueChange}
            type="tel"
            inputRef={ref}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start" style={{ marginRight: '2px' }}>
                        <Select
                            MenuProps={{
                                style: {
                                    borderRadius: 0,
                                    height: '300px',
                                    width: '360px',
                                    top: '10px',
                                    left: '-56px',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                            }}
                            IconComponent={UnfoldMoreIcon}
                            sx={{
                                padding: 0,
                                width: 'max-content',
                                fieldset: {
                                    display: 'none',
                                },
                                '&.Mui-focused': {
                                    fieldset: {
                                        display: 'none',
                                    },
                                    outline: 'none',
                                    boxShadow: 'none',
                                    border: 'none',
                                },
                                '.MuiSelect-select': {
                                    padding: '0px 12px',
                                    paddingRight: '24px !important',
                                    borderRadius: 0,
                                },
                                svg: {
                                    right: 0,
                                },
                            }}
                            value={country.iso2}
                            onChange={e => setCountry(e.target.value)}
                            renderValue={value => (
                                <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                                    <FlagImage
                                        iso2={value}
                                        style={{ display: 'flex', width: '24px', height: '24px' }}
                                    />
                                    <Typography>+{country.dialCode}</Typography>
                                </Stack>
                            )}>
                            {defaultCountries.map(c => {
                                const country = parseCountry(c);
                                return (
                                    <MenuItem
                                        key={country.iso2}
                                        value={country.iso2}
                                        sx={{
                                            padding: '8px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            flexDirection: 'row',
                                        }}>
                                        <FlagImage iso2={country.iso2} style={{ marginRight: '8px' }} />
                                        <Typography
                                            marginRight="8px"
                                            fontSize={14}
                                            color={theme.palette.neutralBlack.main}
                                            fontWeight={700}>
                                            {country.name}
                                        </Typography>
                                        <Box
                                            sx={{
                                                border: `1px solid ${theme.palette.border.main}`,
                                                padding: '2px 4px',
                                                borderRadius: '4px',
                                            }}>
                                            <Typography color={theme.palette.neutralBlack.main}>+{country.dialCode}</Typography>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    );
});

// eslint-disable-next-line react/display-name
const InputPhone = React.forwardRef(({ placeholderTextColor, ...props }: Props, ref) => {
    return (
        <FormGroup sx={{ width: '100%' }}>
            <Controller
                name={props.name}
                control={props.control}
                defaultValue={props.defaultValue}
                rules={{
                    required: { value: props.required, message: 'Le numéro de téléphone est requis' },
                    validate: {
                        regex: val => {
                            // If there's a value (including country code), validate the phone number format
                            if (!!val && val.length > 0) {
                                // Check if the value contains only country code (like "+33") without actual phone number
                                const cleanValue = val.replace(/\s+/g, ''); // Remove spaces
                                if (cleanValue.match(/^\+\d{1,4}$/) && props.required) {
                                    return 'Le numéro de téléphone est requis';
                                }

                                // For phone numbers longer than just country code, validate format
                                if (val.length > 5) {
                                    if (!isPossiblePhoneNumber(val)) {
                                        return 'Le format du numéro de téléphone est incorrect';
                                    }
                                }
                            }
                            return true;
                        },
                    },
                }}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                    return (
                        <>
                            {props.label && (
                                <FormLabel htmlFor={props.name}>{props.label}</FormLabel>
                            )}
                            {props.viewOnly ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {formatPhoneNumberIntl(value) || '-'}
                                </Typography>
                            ) : (
                                <>
                                    <UncontrolledInputPhone
                                        countryCode={props.countryCode}
                                        disabled={props.disabled}
                                        value={value}
                                        onChange={onChange}
                                        error={!!error}
                                        ref={ref}
                                    />
                                    {error?.message && (
                                        <FormHelperText error>{error.message}</FormHelperText>
                                    )}
                                    {!error && props.helperText && (
                                        <FormHelperText>{props.helperText}</FormHelperText>
                                    )}
                                </>
                            )}
                        </>
                    );
                }}
            />
        </FormGroup>
    );
});

export default InputPhone;
